/**
 * Schedule-items fetch window + its size constants.
 *
 * Deliberately a LEAF module: no imports beyond the standard library. `features/schedules/constants`
 * pulls in `rrule`, so anything that must run from the main bundle — `useSchedulesPrefetch`, which
 * AppShell mounts — cannot import from there without dragging ~50 kB of date-recurrence code into
 * the entry chunk. Keeping these here lets the page hook and the prefetch share ONE definition
 * instead of duplicating date arithmetic, which is exactly the kind of thing that drifts silently
 * and turns a prefetch into a cache miss.
 *
 * `constants.ts` re-exports these for existing consumers.
 */

/**
 * Fetch cap for schedule items. The backend costs the same for any value — the marginal cost is
 * response payload. 5000 bounds the worst case at ~2-4 MB raw (~300-400 KB gzipped); past it,
 * `pageInfo.hasNextPage` drives a visible truncation warning instead of silently dropping events.
 */
export const SCHEDULE_ITEMS_FETCH_LIMIT = 5000;

/**
 * Calendar fetch window: bounded backward because past events accumulate for the life of a
 * schedule; wide forward to cover long-horizon planners and yearly recurrences whose first fire is
 * ~12 months out.
 */
export const SCHEDULE_ITEMS_PAST_MONTHS = 2;
export const SCHEDULE_ITEMS_FUTURE_YEARS = 5;

/**
 * Fetch window for schedule items, anchored to day boundaries. The backend honors any window when
 * both dates are sent (its 8-month auto-extension only fires when endDate is omitted —
 * schedule.service.ts getScheduleItems), so the width is purely a payload choice.
 *
 * Day boundaries matter: ms-precision dates would give every consumer mount (ScheduleDetailPanel,
 * EditEventDialog, and the idle prefetch) different query variables → separate Apollo cache entries
 * → a duplicate full fetch per consumer. Midnight-anchored dates are stable within a calendar day,
 * so all consumers share one cache entry.
 */
export function getScheduleItemsWindow(now = new Date()): { startDate: string; endDate: string } {
  const start = new Date(now);
  start.setMonth(start.getMonth() - SCHEDULE_ITEMS_PAST_MONTHS);
  start.setHours(0, 0, 0, 0);
  const end = new Date(now);
  end.setFullYear(end.getFullYear() + SCHEDULE_ITEMS_FUTURE_YEARS);
  end.setHours(23, 59, 59, 999);
  return { startDate: start.toISOString(), endDate: end.toISOString() };
}
